import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import useMediaQuery from '../../hooks/useMediaQuery';
import {
  fetchSeasons,
  selectSeason,
  selectSeasonLoading,
  selectSeasonError,
  selectAvailableSeasons,
  setSeason,
  getWeekNumber,
  getDateOfISOWeek,
} from '../../store/slices/seasonSlice';
import './SeasonSelector.css';

// Register Spanish (Chile) locale
registerLocale('es', es);

const SeasonSelector = () => {
  const dispatch = useDispatch();
  const selected = useSelector(selectSeason);
  const loading = useSelector(selectSeasonLoading);
  const error = useSelector(selectSeasonError);
  const seasons = useSelector(selectAvailableSeasons);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!seasons.length) {
      dispatch(fetchSeasons());
    }
  }, [dispatch, seasons.length]);

  // Initialize selected date from Redux state (year/week)
  useEffect(() => {
    if (selected) {
      // Convert year/week to a representative date (Monday of that week)
      const date = getDateOfISOWeek(selected.week, selected.year);
      setSelectedDate(date);
    }
  }, [selected]);

  // Calculate min/max dates from available seasons
  const { minDate, maxDate } = useMemo(() => {
    if (!seasons.length) return { minDate: null, maxDate: null };
    
    const sortedSeasons = [...seasons].sort((a, b) => a.anio - b.anio);
    const firstSeason = sortedSeasons[0];
    const lastSeason = sortedSeasons[sortedSeasons.length - 1];
    
    const min = getDateOfISOWeek(firstSeason.semana_inicio, firstSeason.anio);
    const max = getDateOfISOWeek(lastSeason.semana_fin, lastSeason.anio);
    // Add 6 days to get end of week
    max.setDate(max.getDate() + 6);
    
    return { minDate: min, maxDate: max };
  }, [seasons]);

  // Calculate current week number for display
  const displayWeek = useMemo(() => {
    if (!selectedDate) return null;
    return getWeekNumber(selectedDate);
  }, [selectedDate]);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleApply = () => {
    if (!selectedDate) return;
    
    const year = selectedDate.getFullYear();
    const week = getWeekNumber(selectedDate);
    
    dispatch(setSeason({ year, week }));
    setOpen(false);
  };

  const handleCancel = () => {
    // Reset to current Redux state without applying changes
    if (selected) {
      const date = getDateOfISOWeek(selected.week, selected.year);
      setSelectedDate(date);
    }
    setOpen(false);
  };

  // Navigation logic
  const currentSeasonIndex = useMemo(() => {
    if (!selected) return -1;
    return seasons.findIndex((s) => s.anio === selected.year);
  }, [seasons, selected]);

  const currentSeason = useMemo(() => {
    if (currentSeasonIndex === -1) return null;
    return seasons[currentSeasonIndex];
  }, [seasons, currentSeasonIndex]);

  const isFirstWeek = useMemo(() => {
    if (!selected || !currentSeason) return true;
    return selected.week === currentSeason.semana_inicio;
  }, [selected, currentSeason]);

  const isLastWeek = useMemo(() => {
    if (!selected || !currentSeason) return true;
    return selected.week === currentSeason.semana_fin;
  }, [selected, currentSeason]);

  const handlePreviousWeek = () => {
    if (!selected || !currentSeason || isFirstWeek) return;
    
    // Move back 7 days
    const currentDate = getDateOfISOWeek(selected.week, selected.year);
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    
    const newYear = newDate.getFullYear();
    const newWeek = getWeekNumber(newDate);
    
    dispatch(setSeason({ year: newYear, week: newWeek }));
  };

  const handleNextWeek = () => {
    if (!selected || !currentSeason || isLastWeek) return;
    
    // Move forward 7 days
    const currentDate = getDateOfISOWeek(selected.week, selected.year);
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    
    const newYear = newDate.getFullYear();
    const newWeek = getWeekNumber(newDate);
    
    dispatch(setSeason({ year: newYear, week: newWeek }));
  };

  return (
    <div className="season-selector">
      <button
        type="button"
        className="season-selector__nav-btn"
        onClick={handlePreviousWeek}
        disabled={loading || !selected || isFirstWeek}
        title="Semana anterior"
      >
        <ChevronLeft size={16} />
      </button>

      <button
        type="button"
        className="season-selector__trigger"
        onClick={() => setOpen((prev) => !prev)}
        disabled={loading}
      >
        {loading && <span className="season-selector__status">Cargando...</span>}
        {!loading && selected && (
          <span className="season-selector__label">
            {isMobile ? (
              <>{selected.year} / {selected.week}</>
            ) : (
              <>Temporada {selected.year} · Semana {selected.week}</>
            )}
          </span>
        )}
        {!loading && !selected && (
          <span className="season-selector__status">{isMobile ? 'Sin temp.' : 'Sin temporada'}</span>
        )}
      </button>

      <button
        type="button"
        className="season-selector__nav-btn"
        onClick={handleNextWeek}
        disabled={loading || !selected || isLastWeek}
        title="Semana siguiente"
      >
        <ChevronRight size={16} />
      </button>

      {open && (
        <div className="season-selector__panel">
          <div className="season-selector__header">
            <h4>Seleccionar fecha</h4>
            {error && <span className="season-selector__error">{error}</span>}
          </div>

          <div className="season-selector__calendar-container">
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              dateFormat="dd/MM/yyyy"
              locale="es"
              placeholderText="Seleccionar fecha"
              minDate={minDate}
              maxDate={maxDate}
              inline
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              calendarClassName="season-calendar"
            />
            
            {displayWeek && (
              <div className="season-selector__week-display">
                <span className="week-label">Semana:</span>
                <span className="week-number">{displayWeek}</span>
              </div>
            )}
          </div>

          <div className="season-selector__actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={handleCancel}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleApply}
              disabled={!selectedDate}
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonSelector;
